(() => {
    const debounce = (fn, wait) => {
        let t;
        const d = (...args) => {
            clearTimeout(t);
            t = setTimeout(() => fn(...args), wait);
        };
        d.cancel = () => clearTimeout(t);
        return d;
    };

    class AnnouncementMarquee extends HTMLElement {
        connectedCallback() {
            this.wrapper = this.querySelector('[ref="wrapper"]');
            this.content = this.querySelector('[ref="content"]');
            this.baseItems = this.querySelector('[ref="items[]"]');

            if (!this.wrapper || !this.content || !this.baseItems) return;

            this._lastContainerWidth = null;
            this._lastItemsWidth = null;

            this._onResize = debounce(() => this._reflow(true), 150);

            window.addEventListener('resize', this._onResize);

            this._ro = new ResizeObserver(() => this._onResize());
            this._ro.observe(this);
            this._ro.observe(this.baseItems);

            this._init();
        }

        disconnectedCallback() {
            window.removeEventListener('resize', this._onResize);
            this._ro?.disconnect();
            this._ro = null;
        }

        async _init() {
            await this._reflow(false);
        }

        get _clonedContent() {
            const last = this.wrapper.lastElementChild;
            return last && last !== this.content ? last : null;
        }

        _duplicateContent() {
            this._clonedContent?.remove();

            const clone = this.content.cloneNode(true);
            clone.setAttribute('aria-hidden', 'true');
            clone.querySelectorAll('[ref]').forEach((el) => el.removeAttribute('ref'));
            this.wrapper.appendChild(clone);
        }

        _addRepeatedItems(addCount) {
            for (let i = 0; i < addCount; i++) {
                this.content.appendChild(this.baseItems.cloneNode(true));
            }
        }

        _removeRepeatedItems(removeCount) {
            const items = Array.from(this.content.children).filter((el) => el.tagName === 'UL');
            const canRemove = Math.max(0, items.length - 1);
            const toRemove = Math.min(removeCount, canRemove);

            for (let i = 0; i < toRemove; i++) {
                const last = this.content.querySelector('ul:last-child');
                last?.remove();
            }
        }

        async _measure() {
            await new Promise((r) => requestAnimationFrame(r));
            await new Promise((r) => requestAnimationFrame(r));

            const containerWidth = Math.round(this.getBoundingClientRect().width);
            const itemsWidth = Math.round(this.baseItems.getBoundingClientRect().width);

            return { containerWidth, itemsWidth };
        }

        _setVars() {
            const gap = getComputedStyle(this.baseItems).columnGap || getComputedStyle(this.baseItems).gap || '0px';
            this.style.setProperty('--am-gap', gap);

            const isDesktop = window.matchMedia('(min-width: 750px)').matches;

            const secRaw = isDesktop
                ? Number(this.dataset.speedDesktop || this.dataset.speed || 50)
                : Number(this.dataset.speedMobile || this.dataset.speed || 50);

            const sec = Math.min(100, Math.max(2, secRaw));
            this.style.setProperty('--am-speed', `${sec}s`);
        }

        async _reflow(restart) {
            if (this.dataset.enabled !== '1') return;

            this._setVars();

            const { containerWidth, itemsWidth } = await this._measure();
            const safeItemsWidth = itemsWidth > 0 ? itemsWidth : 1;

            const copies = Math.max(1, Math.ceil(containerWidth / safeItemsWidth));
            const target = Math.min(copies, 30);

            const isContainerChanged = this._lastContainerWidth !== containerWidth;
            const isItemsChanged = this._lastItemsWidth !== itemsWidth;

            this._lastContainerWidth = containerWidth;
            this._lastItemsWidth = itemsWidth;

            if (!isContainerChanged && !isItemsChanged) return;

            const current = this.content.querySelectorAll('ul').length || 1;

            if (target > current) this._addRepeatedItems(target - current);
            else if (target < current) this._removeRepeatedItems(current - target);

            this._duplicateContent();
        }
    }

    if (!customElements.get('announcement-marquee')) {
        customElements.define('announcement-marquee', AnnouncementMarquee);
    }
})();